import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLight } from "@/context/LightContext";
import { ArrowRight } from "lucide-react";
import {
    playWarmChime,
    playDoorTransition,
} from "@/lib/portalAudio";

/**
 * Small, secondary 3D gateway on the hero.
 * Reveals ~2s after the light-switch ritual, click dims lights + navigates.
 *
 * Size is a hard pixel cap — 180×240 desktop / 130×170 mobile — so it
 * never competes with the headline or CTA.
 *
 * Extras:
 *  - Hover on desktop reveals a green light peek through the inner panel.
 *  - A warm synthesised chime plays once when the door first reveals.
 */
export default function PortalDoor({
    href = "/real-estate",
    theme = "green",
    variant = "both", // 'both' | 'desktop' | 'mobile'
    label = "For Real Estate",
    sublabel = "Agent or broker?",
    // Desktop pixel offset from viewport right edge. Two doors on the hero
    // pass different values so they sit side by side.
    desktopRight = "60px",
    // Whether this door instance owns the shared dim overlay + chime side-
    // effect. Set to false on additional doors that share the hero.
    ownsSideEffects = true,
}) {
    const { activated, deactivate, setTheme } = useLight();
    const navigate = useNavigate();
    const [dimming, setDimming] = useState(false);
    const chimeFiredRef = useRef(false);

    // Play the chime exactly ONCE per session when the door first appears
    useEffect(() => {
        if (!activated) return;
        if (chimeFiredRef.current) return;
        if (!ownsSideEffects) return;
        if (variant === "mobile") return; // avoid double-fire from twin mounts
        chimeFiredRef.current = true;
        // Delay chime slightly to sync with the fade-in reveal
        const t = setTimeout(() => playWarmChime(), 900);
        return () => clearTimeout(t);
    }, [activated, variant, ownsSideEffects]);

    if (!activated) return null;

    const handleStepThrough = () => {
        if (dimming) return;
        setDimming(true);
        // Fire the door-transition sound on the click gesture (browsers
        // require user gesture for audio). Respects mute + reduced-motion.
        playDoorTransition();
        deactivate();
        setTheme(theme);
        setTimeout(() => {
            navigate(href);
            setTimeout(() => setDimming(false), 900);
        }, 900);
    };

    const handleKey = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleStepThrough();
        }
    };

    // Shared inner door markup (frame + labels)
    const DoorBlock = ({ frameWidth, frameHeight }) => (
        <>
            <p
                className="text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-[var(--accent-gold)] opacity-90 mb-2 font-body"
                data-testid="portal-door-eyebrow"
            >
                {label}
            </p>

            <a
                href={href}
                onClick={(e) => {
                    e.preventDefault();
                    handleStepThrough();
                }}
                onKeyDown={handleKey}
                aria-label={`Step through the door to ${label}`}
                data-peek={theme}
                className="portal-door outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-gold)] focus-visible:ring-offset-4 focus-visible:ring-offset-transparent rounded-t-full"
            >
                <div
                    className="portal-door__frame"
                    style={{ width: frameWidth, height: frameHeight }}
                >
                    <div className="portal-door__edge" aria-hidden />
                    <div className="portal-door__inner">
                        <div className="portal-door__glow" aria-hidden />
                        <div className="portal-door__preview" aria-hidden />
                        <div className="portal-door__panel-line" aria-hidden />
                    </div>
                    <div className="portal-door__handle" aria-hidden />
                </div>
            </a>

            <p
                className="mt-3 text-[13px] leading-snug text-[var(--text-primary)] max-w-[170px] text-center font-body"
                data-testid="portal-door-caption"
            >
                {sublabel}{" "}
                <span className="text-[var(--accent-gold)] inline-flex items-center gap-1 whitespace-nowrap font-semibold">
                    Step through <ArrowRight size={12} />
                </span>
            </p>
        </>
    );

    const showDesktop = variant === "both" || variant === "desktop";
    const showMobile = variant === "both" || variant === "mobile";

    return (
        <>
            {/* Desktop — small badge on the right side, vertically centred on the headline block */}
            {showDesktop && (
                <AnimatePresence>
                    {activated && !dimming && (
                        <motion.div
                            key="portal-door"
                            initial={{ opacity: 0, scale: 0.85, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.35 } }}
                            transition={{ duration: 1.2, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="hidden lg:flex flex-col items-center absolute z-20"
                            style={{
                                right: desktopRight,
                                top: "42%",
                                transform: "translateY(-50%)",
                            }}
                            data-testid={`portal-door-${theme}`}
                        >
                            <DoorBlock frameWidth="150px" frameHeight="220px" />
                        </motion.div>
                    )}
                </AnimatePresence>
            )}

            {/* Mobile — sits below the hero CTAs, side-by-side with the sibling door */}
            {showMobile && (
                <AnimatePresence>
                    {activated && !dimming && (
                        <motion.div
                            key="portal-door-mobile"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, transition: { duration: 0.35 } }}
                            transition={{ duration: 1.1, delay: 0.7 }}
                            className="lg:hidden flex flex-col items-center"
                            style={{ width: "130px" }}
                            data-testid={`portal-door-mobile-${theme}`}
                        >
                            <DoorBlock frameWidth="110px" frameHeight="150px" />
                        </motion.div>
                    )}
                </AnimatePresence>
            )}

            {/* Dim overlay — only one door owns this to avoid duplicate overlays */}
            {ownsSideEffects && variant !== "mobile" && (
                <AnimatePresence>
                    {dimming && (
                        <motion.div
                            key="portal-dim"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.6 }}
                            className="portal-dim"
                            data-testid="portal-dim"
                        />
                    )}
                </AnimatePresence>
            )}
        </>
    );
}
